import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { WorkDay, WorkScheduleExemption, WorkScheduleExemptionType } from '@apaq/orgkit-js';
import { addDays, getUnixTime, set } from 'date-fns';
import { DialogRef } from 'src/app/modules/dialog/dialog-ref';
import { DATA, DIALOG, DialogService } from 'src/app/modules/dialog/dialog.service';

export interface Data {
  exemption: WorkScheduleExemption;
}

@Component({
  selector: 'app-exemption-editor-dialog',
  templateUrl: './exemption-editor-dialog.component.html',
  styleUrls: ['./exemption-editor-dialog.component.scss']
})
export class ExemptionEditorDialogComponent implements OnInit {

  form: FormGroup;
  exemption?: WorkScheduleExemption;
  isNew = false;
  
  constructor(fb: FormBuilder,
    @Inject(DIALOG) private dialog: DialogRef,
    @Inject(DATA) private data: Data) {
    this.form = fb.group({
      'description': [''],
      'startDate': [new Date()],
      'endDate': [null],
      'recurrenceRule': [null],
      'workDay': [null],
      'type': ['DayOff']
    });

    this.form.get('type')?.valueChanges.subscribe(_ => this.handleTypeChange());
    this.form.get('startDate')?.valueChanges.subscribe(_ => this.trimEndDate());
    this.form.get('endDate')?.valueChanges.subscribe(_ => this.trimStartDate());
  }

  ngOnInit(): void {
    if(this.data?.exemption) {
      this.form.patchValue(this.data.exemption);
    } else {
      this.isNew = true;
    }

    this.handleTypeChange();
    
  }

  get type() {
    return this.form.get('type')?.value;
  }

  get recurringEnabled() {
    return  this.form.get('recurrenceRule')?.value != null;
  }

  cancel() {
    this.dialog.close();
  }
  save() {
    this.dialog.close({action: 'Save', entity: this.form.value});
  }

  delete() {
    this.dialog.close({action: 'Delete'});
  }

  onCheckedChange(event: Event) {
    const recurringEnabled = (event as CustomEvent).detail;
    if(!recurringEnabled) {
      this.form.get('recurrenceRule')?.setValue(null);
      this.form.get('recurrenceRule')?.disable();
    } else {
      this.form.get('recurrenceRule')?.setValue('FREQ=WEEKLY;INTERVAL=1');
      this.form.get('recurrenceRule')?.enable();
    }
  }

  handleTypeChange() {
    const type = this.form.get('type')?.value as WorkScheduleExemptionType;
    const startDate = this.form.get('startDate')?.value as Date;

    switch(type) {
      case 'DayOff':
        this.form.get('endDate')?.setValue(startDate);
        this.form.get('workDay')?.setValue(this.createNonWorkDay());
        break;
      case 'Vacation':
        this.form.get('endDate')?.setValue(addDays(startDate, 1));
        this.form.get('recurrenceRule')?.setValue(null);
        this.form.get('workDay')?.setValue(this.createNonWorkDay());
        break;
      case 'AdjustedWorkDay':
        this.form.get('endDate')?.setValue(null);
        break;
    }

    if(this.form.get('recurrenceRule')?.value === null) {
      this.form.get('recurrenceRule')?.disable();
    };

    this.trimEndDate();
  } 

  trimStartDate() {
    const startDate: Date = this.form.get('startDate')?.value;
    const endDate: Date = this.form.get('endDate')?.value;

    if(endDate != null) {
      const newStartDate = new Date(Math.min(startDate.getTime(), endDate.getTime()));

      if(startDate.getTime() != newStartDate.getTime()) {
        this.form.get('startDate')?.setValue(newStartDate);
      }  
    }
  }

  trimEndDate() {
    const startDate: Date = this.form.get('startDate')?.value;
    let endDate: Date = this.form.get('endDate')?.value;
    const type = this.form.get('type')?.value as WorkScheduleExemptionType;
    const reccurenceRule = this.form.get('recurrenceRule')?.value as string;

    if(type === 'DayOff' || 
      (type === 'AdjustedWorkDay' && reccurenceRule == null)) {
      this.form.get('endDate')?.setValue(startDate);
    } else if(endDate != null) {
      const newEndDate = new Date(Math.max(startDate.getTime(), endDate.getTime()));
      
      if(endDate.getTime() != newEndDate.getTime()) {
        this.form.get('endDate')?.setValue(newEndDate);
      }
    }
  }

  private createNonWorkDay() {
    return {
      startTime: set(getUnixTime(0), {hours: 0, minutes: 0, seconds: 0, milliseconds: 0}),
      endTime: set(getUnixTime(0), {hours: 0, minutes: 0, seconds: 0, milliseconds: 0}),
    }
  }

  private createDefaultWorkDay(): WorkDay {
    return {
      startTime: set(getUnixTime(0), {hours: 6, minutes: 0, seconds: 0, milliseconds: 0}),
      endTime: set(getUnixTime(0), {hours: 15, minutes: 0, seconds: 0, milliseconds: 0}),
    }
  }


}
